import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Play, Search, Settings, Upload, Moon, Sun, Monitor } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { LoginArea } from '@/components/auth/LoginArea';
import { useTheme } from '@/hooks/useTheme';
import { VERSION } from '@/lib/version';
import { cn } from '@/lib/utils';

function ThemeMenu() {
  const { theme, setTheme } = useTheme();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Toggle theme">
          {theme === 'dark' ? <Moon className="h-5 w-5" /> : theme === 'light' ? <Sun className="h-5 w-5" /> : <Monitor className="h-5 w-5" />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => setTheme('light')}>
          <Sun className="h-4 w-4" /> Light
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setTheme('dark')}>
          <Moon className="h-4 w-4" /> Dark
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setTheme('system')}>
          <Monitor className="h-4 w-4" /> System
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SearchForm({ className, trailing }: { className?: string; trailing?: ReactNode }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  const onSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    if (q) {
      navigate(`/search?q=${encodeURIComponent(q)}`);
      setQuery('');
    }
  };

  return (
    <form onSubmit={onSearch} className={className}>
      <div className="flex items-center gap-2">
        <Input
          type="search"
          placeholder="Search videos by title or description…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="h-9"
          aria-label="Search videos"
        />
        <Button type="submit" size="icon" variant="secondary" className="shrink-0 h-9 w-9" aria-label="Search">
          <Search className="h-4 w-4" />
        </Button>
        {trailing}
      </div>
    </form>
  );
}

function VersionBadge({ className }: { className?: string }) {
  return (
    <Link
      to="/changelog"
      className={cn(
        'shrink-0 rounded-full bg-secondary px-1.5 py-0.5 font-mono text-[10px] font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground',
        className,
      )}
      title="Changelog"
    >
      v{VERSION}
    </Link>
  );
}

export function Header() {
  return (
    <header className="sticky top-0 z-40 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <div className="flex h-14 items-center gap-2 px-3 sm:gap-3 sm:px-6">
        <span className="flex items-center gap-1.5 shrink-0">
          <Link to="/" className="flex items-center gap-1.5" aria-label="VidStr home">
            <span className="flex items-center justify-center rounded-md bg-primary p-1">
              <Play className="h-4 w-4 text-primary-foreground fill-primary-foreground" />
            </span>
            <span className="text-lg font-bold tracking-tight">VidStr</span>
          </Link>
          {/* Next to the logo on sm+; on mobile it lives in the search row */}
          <VersionBadge className="mt-0.5 hidden sm:inline-block" />
        </span>

        <SearchForm className="hidden md:flex flex-1 max-w-xl mx-auto" />

        <div className="flex items-center gap-0.5 sm:gap-1 shrink-0 ml-auto">
          <Button asChild variant="ghost" size="sm" className="gap-1.5">
            <Link to="/upload">
              <Upload className="h-4 w-4" />
              <span className="hidden sm:inline">Upload</span>
            </Link>
          </Button>
          <Button asChild variant="ghost" size="icon" aria-label="Settings">
            <Link to="/settings">
              <Settings className="h-5 w-5" />
            </Link>
          </Button>
          <ThemeMenu />
          <LoginArea className="max-w-28 sm:max-w-40" />
        </div>
      </div>
      {/* Mobile search row */}
      <div className="md:hidden px-3 pb-2.5">
        <SearchForm trailing={<VersionBadge />} />
      </div>
    </header>
  );
}
